// src/pages/register.jsx
import React, { useState } from "react";
import Footer from "../components/footer";
import Hero from "../components/hero";
import ShapesBackground from "../components/shapesbackground";
import SectionHeading from '../components/sectionheading';
import { useLang } from '../context/langcontext';
import emailjs from '@emailjs/browser';

// Images for buttons
import riderImg from "../assets/register/rider.png";
import storeImg from "../assets/register/store.png";

const Register = () => {
  const { t } = useLang();
  const [selectedOption, setSelectedOption] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [riderData, setRiderData] = useState({
    name: "",
    phone: "",
    idNumber: "",
    nationality: "",
    residence: "",
    deliveryCode: "",
  });
  
  const [storeData, setStoreData] = useState({
    storeName: "",
    ownerName: "",
    email: "",
    phone: "",
    address: "",
    cityZone: "",
    category: "",
  });

  const categories = [
    "all",
    "restaurants",
    "fruitsVeg",
    "grocery",
    "meat",
    "chicken",
    "fishSeafood",
    "nuts",
    "spicesCoffee",
    "bakery",
    "cafe",
    "pharmacies",
    "clothes",
    "perfumes",
    "cosmetics",
    "laundry",
    "hotelReservations",
    "rentCars",
    "taxi",
    "health_nutrition",
    "flowers_gifts",
    "dessert",
  ];

  // Info cards content
  const storeCards = [
    {
      title: t("moreCustomersTitle") || "More Customers",
      desc: t("moreCustomersDesc") || "We have many customers in your area looking for food from your restaurant. We help deliver their orders as quickly as possible."
    },
    {
      title: t("customerReachTitle") || "Customer Reach Expansion",
      desc: t("customerReachDesc") || "Easily connect with new local customers and extend your restaurant's reach in your community."
    },
    {
      title: t("higherProfitsTitle") || "Higher Profits",
      desc: t("higherProfitsDesc") || "Easily increase your daily sales through our platform by continuously reaching new customers."
    },
    {
      title: t("streamlinedOrdersTitle") || "Streamlined Orders",
      desc: t("streamlinedOrdersDesc") || "Manage your incoming orders effortlessly through our easy-to-use platform, saving time and reducing errors."
    },
    {
      title: t("effectiveMarketingTitle") || "Effective Marketing",
      desc: t("effectiveMarketingDesc") || "We promote your restaurant on our platform to attract more customers and boost daily orders."
    },
    {
      title: t("fullSupportTitle") || "Full Support",
      desc: t("fullSupportDesc") || "Our team is ready to assist you every step of the way to ensure your success and growth with Selal."
    }
  ];

  const riderCards = [
    {
      title: t("increaseIncomeTitle") || "Increase Your Income",
      desc: t("increaseIncomeDesc") || "Join Selal's network and earn extra income flexibly, according to your available time."
    },
    {
      title: t("communityConnectionsTitle") || "Community Connections",
      desc: t("communityConnectionsDesc") || "Be part of a growing delivery network in your area and build strong connections with local businesses and customers."
    },
    {
      title: t("flexibleWorkTitle") || "Flexible Work",
      desc: t("flexibleWorkDesc") || "Choose your own working hours and manage your delivery trips without complex commitments."
    },
    {
      title: t("skillGrowthTitle") || "Skill Growth",
      desc: t("skillGrowthDesc") || "Enhance your professional skills in logistics, time management, and customer service while earning."
    },
    {
      title: t("reliableSupportTitle") || "Reliable Support",
      desc: t("reliableSupportDesc") || "Our team guides you every step of the way to make sure your deliveries are smooth and efficient."
    },
    {
      title: t("trainingGuidanceTitle") || "Training & Guidance",
      desc: t("trainingGuidanceDesc") || "Get all the tools and training you need to provide excellent service to customers."
    }
  ];

  const handleRiderChange = (e) => {
    setRiderData({ ...riderData, [e.target.name]: e.target.value });
  };

  const handleStoreChange = (e) => {
    setStoreData({ ...storeData, [e.target.name]: e.target.value });
  };

  const handleRiderSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const serviceID = 'service_6juzkg8';
      const templateID = 'template_0m0ync8';
      const publicKey = '9fxVTVWsjvliV6c0h';

      const templateParams = {
        to_email: 'info@selalom.com',
        registration_type: 'Delivery Representative',
        name: riderData.name,
        phone: riderData.phone,
        id_number: riderData.idNumber,
        nationality: riderData.nationality,
        residence: riderData.residence,
        delivery_code: riderData.deliveryCode || 'N/A',
      };

      await emailjs.send(serviceID, templateID, templateParams, publicKey);
      
      // Show success popup
      showSuccessPopup(t("riderSuccess") || "Delivery Rider registered successfully!");
      
      setRiderData({
        name: "",
        phone: "",
        idNumber: "",
        nationality: "",
        residence: "",
        deliveryCode: "",
      });
      setSelectedOption("");
    } catch (error) {
      console.error("Error sending registration:", error);
      showErrorPopup(t("messageError") || "Failed to send registration. Please try again or contact us at info@selalom.com");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStoreSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const serviceID = 'service_u3dl6or';
      const templateID = 'template_9lil65m';
      const publicKey = 'lroI9yz4b_daCSkan';

      const templateParams = {
        to_email: 'info@selalom.com',
        registration_type: 'Store Registration',
        store_name: storeData.storeName,
        owner_name: storeData.ownerName,
        email: storeData.email,
        phone: storeData.phone,
        address: storeData.address,
        city_zone: storeData.cityZone,
        category: t(storeData.category),
      };

      await emailjs.send(serviceID, templateID, templateParams, publicKey);
      
      // Show success popup
      showSuccessPopup(t("storeSuccess") || "Store registered successfully!");
      
      setStoreData({
        storeName: "",
        ownerName: "",
        email: "",
        phone: "",
        address: "",
        cityZone: "",
        category: "",
      });
      setSelectedOption("");
    } catch (error) {
      console.error("Error sending registration:", error);
      showErrorPopup(t("messageError") || "Failed to send registration. Please try again or contact us at info@selalom.com");
    } finally {
      setIsSubmitting(false);
    }
  };

  const showSuccessPopup = (message) => {
    const popup = document.createElement('div');
    popup.className = 'custom-popup success-popup';
    popup.innerHTML = `
      <div class="popup-content">
        <div class="popup-icon success-icon">✓</div>
        <h3>${t("successTitle") || "Success!"}</h3>
        <p>${message}</p>
        <button class="popup-btn" onclick="this.parentElement.parentElement.remove()">${t("closeBtn") || "Close"}</button>
      </div>
    `;
    document.body.appendChild(popup);
    setTimeout(() => popup.classList.add('show'), 10);
  };

  const showErrorPopup = (message) => {
    const popup = document.createElement('div');
    popup.className = 'custom-popup error-popup';
    popup.innerHTML = `
      <div class="popup-content">
        <div class="popup-icon error-icon">✕</div>
        <h3>${t("errorTitle") || "Error"}</h3>
        <p>${message}</p>
        <button class="popup-btn" onclick="this.parentElement.parentElement.remove()">${t("closeBtn") || "Close"}</button>
      </div>
    `;
    document.body.appendChild(popup);
    setTimeout(() => popup.classList.add('show'), 10);
  };

  return (
    <>
      <ShapesBackground />

      <Hero
        heroImage={require('../assets/phone/register.png')}
        customHeading={t("registerHeroHeading")}
        customSubtext={t("registerCustomSubtext")}
        customDescription={t("registerHeroDescription")}
        hideButtons={["register"]}
      />

      {/* Register Choice Section */}
      <section className="register-choice">
        <SectionHeading text={t("registerTitle") || "Register"} />
        <div className="register-buttons">
          <button
            className={selectedOption === "rider" ? "register-btn active" : "register-btn"}
            onClick={() => setSelectedOption("rider")}
          >
            <p className="register-btn-text">
              <strong>{t("riderBtnText") || "Be a delivery representative and earn income with Selal"}</strong>
            </p>
            <img src={riderImg} alt="Rider" className="register-btn-img"/>
            <strong>{t("registerRiderBtn") || "Register as Delivery Representative"}</strong>
          </button>
          
          <button
            className={selectedOption === "store" ? "register-btn active" : "register-btn"}
            onClick={() => setSelectedOption("store")}
          >
            <p className="register-btn-text">
              <strong>{t("storeBtnText") || "Expand your business by reaching more customers with us"}</strong>
            </p>
            <img src={storeImg} alt="Store" className="register-btn-img"/>
            <strong>{t("registerStoreBtn") || "Register Your Store"}</strong>
          </button>
        </div>
      </section>

      {/* Rider Registration Form with Info Cards */}
      {selectedOption === "rider" && (
        <div className="form-container">
          {/* Left Info Cards */}
          <div className="info-cards">
            {riderCards.slice(0, 3).map((card, index) => (
              <div key={index} className="info-card">
                <h4>{card.title}</h4>
                <p>{card.desc}</p>
              </div>
            ))}
          </div>

          {/* Center Form */}
          <div className="contact-form">
            <h2>{t("riderFormHeading") || "Join Our Team!"}</h2>
            <p>{t("riderFormDesc") || "If you own a car or motorcycle and are looking for an opportunity to increase your income, you've come to the right place. Register now!"}</p>
            <form onSubmit={handleRiderSubmit}>
              <input 
                type="text" 
                name="name" 
                placeholder={t("riderName") || "Rider Name"} 
                value={riderData.name} 
                onChange={handleRiderChange} 
                required 
                disabled={isSubmitting}
              />
              <input 
                type="text" 
                name="phone" 
                placeholder={t("riderPhone") || "Phone Number"} 
                value={riderData.phone} 
                onChange={handleRiderChange} 
                required 
                disabled={isSubmitting}
              />
              <input 
                type="text" 
                name="idNumber" 
                placeholder={t("riderID") || "ID Number"} 
                value={riderData.idNumber} 
                onChange={handleRiderChange} 
                required 
                disabled={isSubmitting}
              />
              <input 
                type="text" 
                name="nationality" 
                placeholder={t("riderNationality") || "Nationality"} 
                value={riderData.nationality} 
                onChange={handleRiderChange} 
                required 
                disabled={isSubmitting}
              />
              <input 
                type="text" 
                name="residence" 
                placeholder={t("riderResidence") || "Residence"} 
                value={riderData.residence} 
                onChange={handleRiderChange} 
                required 
                disabled={isSubmitting}
              />
              <input 
                type="text" 
                name="deliveryCode" 
                placeholder={t("riderDeliveryCode") || "Delivery Company Code (optional)"} 
                value={riderData.deliveryCode} 
                onChange={handleRiderChange} 
                disabled={isSubmitting}
              />
              <button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (t("sending") || "Sending...") : (t("submitBtn") || "Submit")}
              </button>
            </form>
          </div>

          {/* Right Info Cards */}
          <div className="info-cards">
            {riderCards.slice(3, 6).map((card, index) => (
              <div key={index} className="info-card">
                <h4>{card.title}</h4>
                <p>{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Store Registration Form with Info Cards */}
      {selectedOption === "store" && (
        <div className="form-container">
          {/* Left Info Cards */}
          <div className="info-cards">
            {storeCards.slice(0, 3).map((card, index) => (
              <div key={index} className="info-card">
                <h4>{card.title}</h4>
                <p>{card.desc}</p>
              </div>
            ))}
          </div>

          {/* Center Form */}
          <div className="contact-form">
            <h2>{t("storeFormHeading") || "Start Your Journey to Success!"}</h2>
            <p>{t("storeFormDesc") || "Join us to boost your business by easily reaching your customers, increasing your sales, and promoting your online business. Your inspiring story starts here."}</p>
            <form onSubmit={handleStoreSubmit}>
              <input 
                type="text" 
                name="storeName" 
                placeholder={t("storeName") || "Store Name"} 
                value={storeData.storeName} 
                onChange={handleStoreChange} 
                required 
                disabled={isSubmitting}
              />
              <input 
                type="text" 
                name="ownerName" 
                placeholder={t("ownerName") || "Owner Name"} 
                value={storeData.ownerName} 
                onChange={handleStoreChange} 
                required 
                disabled={isSubmitting}
              />
              <input 
                type="email" 
                name="email" 
                placeholder={t("email") || "Email"} 
                value={storeData.email} 
                onChange={handleStoreChange} 
                required 
                disabled={isSubmitting}
              />
              <input 
                type="text" 
                name="phone" 
                placeholder={t("storePhone") || "Phone Number"} 
                value={storeData.phone} 
                onChange={handleStoreChange} 
                required 
                disabled={isSubmitting}
              />
              <textarea 
                name="address" 
                placeholder={t("storeAddress") || "Store Address"} 
                value={storeData.address} 
                onChange={handleStoreChange} 
                required 
                disabled={isSubmitting}
                rows="3"
              />
              <input 
                type="text" 
                name="cityZone" 
                placeholder={t("cityZone") || "City / Zone"} 
                value={storeData.cityZone} 
                onChange={handleStoreChange} 
                required 
                disabled={isSubmitting}
              />
              <select 
                name="category" 
                value={storeData.category} 
                onChange={handleStoreChange} 
                required
                disabled={isSubmitting}
              >
                <option value="" disabled>{t("selectCategory") || "Select a Category"}</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{t(`category${cat}`) || cat}</option>
                ))}
              </select>
              <button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (t("sending") || "Sending...") : (t("submitBtn") || "Submit")}
              </button>
            </form>
          </div>

          {/* Right Info Cards */}
          <div className="info-cards">
            {storeCards.slice(3, 6).map((card, index) => (
              <div key={index} className="info-card">
                <h4>{card.title}</h4>
                <p>{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default Register;